/**
 * @module app.core
 */

import { autoinject, bindable, bindingMode } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting, StandardLoadOptions } from 'app/shared/lib/essette-site';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { apiCall } from 'app/shared/lib/api/api';
import { INotesConfig } from 'app/core/models/essette-interfaces';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { INoteSearchFilter, NoteService } from 'app/core/services/note-service';
import { Toaster } from 'app/shared/lib/toaster';
import { defer } from 'app/shared/lib/decorators';
import { confirmation, ConfirmationOptions, IConfirmationDialogResult } from 'app/shared/elements/ess-confirm';
import { validate } from 'app/shared/elements/ess-rule';
import { IPopup } from 'app/shared/elements/popup-base';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { NoteHistory } from 'app/core/components/notes/note-history'
import { IDialogOptions, IModalData, openGlobalModal } from 'app/shared/elements/ess-global-modal';
import { EventAggregator } from 'aurelia-event-aggregator';
import { NotesReloadEvent } from 'app/core/lib/events';
import { fireEvents } from 'app/shared/lib/fire-events';
import { deepClone } from 'app/shared/lib/utilities';
import { guid } from 'app/shared/lib/guid';
import { UseContainerCache } from 'app/shared/lib/cache/use-container-cache';
import { createNotesCache, INotesCache } from '../../caches/notes-cache';
import { EssTableCustomElement } from 'app/shared/elements/ess-table';
import * as Core from 'app/core/models/essette-api';

interface INoteSavedEvent { targetId: string }
export type NoteSavedEvent = CustomEvent<INoteSavedEvent>;

@autoinject
@UseContainerCache({ cacheFactory: createNotesCache })
export class NotesGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable()
    protected cacheKey: string | null = null;
    declare cache: INotesCache;

    @bindable({ defaultBindingMode: bindingMode.toView })
    public targetId: string | null = null;

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public notesConfig: INotesConfig = {};

    @bindable({ defaultBindingMode: bindingMode.toView })
    public showInactiveToggle: boolean = false;

    @bindable({ defaultBindingMode: bindingMode.toView })
    public includeInactive: boolean = true;

    @bindable({ defaultBindingMode: bindingMode.toView })
    public allowDeactivate: boolean = true;

    @bindable({ defaultBindingMode: bindingMode.toView })
    public canEditNotes: boolean = false;

    @bindable({ defaultBindingMode: bindingMode.toView })
    public pageSize: number = 25;

    @bindable({ defaultBindingMode: bindingMode.toView })
    public readonly: boolean = false;

    @bindable({ defaultBindingMode: bindingMode.toView })
    public configurable: boolean = false;

    @bindable({ defaultBindingMode: bindingMode.toView })
    public allowFlagSelect: boolean = true;

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    protected searchFilters: INoteSearchFilter[] = [];
    protected appliedSearchFilters: INoteSearchFilter[] = [];

    protected showTaskColumn: boolean = true;
    protected showFlagsColumn: boolean = true;
    protected sorting = new Sorting();

    protected editedNote: Core.Note | null = null;

    protected validate = validate;
    protected $isValid: boolean = false;
    protected canAddNote: boolean = true;
    protected modalOpened = false;

    protected selectedFilterCount = 0;
    protected totalFilterCount = 0;
    protected allFiltersSelected = false;

    protected id: guid = guid();

    ////****************** Element References ***************************************////
    private $pager!: EssPagerCustomElement;
    private $saveNoteModal!: IPopup;

    private $noteHistoryModal!: IPopup;
    private $noteHistory!: NoteHistory;

    protected $notesTable!: EssTableCustomElement<Core.Note>;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly $element: Element,
        private readonly toaster: Toaster,
        private readonly service: NoteService,
        private readonly events: EventAggregator,
        ...rest: any[]) {
        super(...rest);
    }

    protected async activate(model: INotesModelData): Promise<void> {
        if(!model) { return; }

        this.targetId = model.targetId ?? this.targetId;
        this.notesConfig = model.notesConfig ?? this.notesConfig;
        this.canEditNotes = model.canEditNotes ?? this.canEditNotes;
        this.showInactiveToggle = model.showInactiveToggle ?? this.showInactiveToggle;
        this.allowDeactivate = model.allowDeactivate ?? this.allowDeactivate;
        this.includeInactive = model.includeInactive ?? this.includeInactive;
        this.readonly = model.readonly ?? this.readonly;
    }

    protected bind(): void {
        if(!this.targetId) { this.logger.warn('Bindable property [target-id] was not set at binding'); }
        if(!this.notesConfig) { this.logger.warn('Bindable property [notes-config] was not set at binding'); }

        if(this.notesConfig?.targetType !== 'CM.Case') {
            this.showTaskColumn = false;
        }
        if(this.notesConfig?.targetType == 'CD.Auth') {
            this.showFlagsColumn = false;
        }

        this.totalFilterCount = this.searchFilters.length;
        this.selectedFilterCount = this.searchFilters.filter(filter => filter.selected).length;
        this.allFiltersSelected = this.totalFilterCount > 0 && this.totalFilterCount === this.selectedFilterCount;
        this.appliedSearchFilters = deepClone(this.searchFilters);

        this.service.configure(this.notesConfig);
        this.checkPermissions();

        // Check if cached notes data is still fresh to use and related to target id
        if(this.cache.targetId === this.targetId && this.cache.isFresh('notes')) {
            if(!!this.cache.sorting) {
                this.sorting = this.cache.sorting;
            }
            if(this.cache.includeInactive != null) {
                this.includeInactive = this.cache.includeInactive;
            }
            this.$pager.page = this.cache.page || 0;
        } else {
            this.deferReload();
        }
    }

    protected attached(): void {
        if(this.sorting.entries.length > 0) {
            this.$notesTable.applySorting(this.sorting);
        }
    }

    ////****************** Events ***************************************************////
    protected handleNoteSaved(event: FormSuccessCustomEvent) {
        this.$saveNoteModal.close();

        if(this.targetId) {
            const detail: INoteSavedEvent = { targetId: this.targetId };
            fireEvents(this.$element, 'note-saved', detail);
        }

        if(event.detail.form.reset) {
            event.detail.form.reset();
        }

        this.deferReload();
    }

    protected toggleFilter(toggleSelectAll: boolean = false): void {
        if(toggleSelectAll) {
            for(const filter in this.searchFilters) {
                this.searchFilters[filter].selected = this.allFiltersSelected;
            }
        }

        this.selectedFilterCount = this.searchFilters.filter(filter => filter.selected).length;
        // check "Select All" box if all filters are selected, or uncheck if not all filters are selected
        if(this.allFiltersSelected && this.selectedFilterCount < this.totalFilterCount) this.allFiltersSelected = false;
        else if(!this.allFiltersSelected && this.selectedFilterCount === this.totalFilterCount) this.allFiltersSelected = true;
    }

    ////****************** Functions ************************************************////
    public async reload(resetPage: boolean = false): Promise<void> {
        if(resetPage && this.$pager) this.$pager.updatePage(0);
        await this.loadNotes();
    }

    protected async applyFilters(): Promise<void> {
        this.appliedSearchFilters = deepClone(this.searchFilters);
        await this.loadNotes();
    }

    private async checkPermissions(): Promise<void> {
        if(!this.notesConfig?.flagCodes) return;
        await this.accountContext.load();
        if(this.notesConfig.flagCodes[0] === 'UM.Summary') this.canAddNote = this.env.application.code === 'Portal.Providers' ? this.accountContext.hasPermission('UM.AuthNote.Insert.PortalSummaryInsert') : this.accountContext.hasPermission('UM.AuthNote.Insert.SummaryInsert');
        else if(this.notesConfig.flagCodes[0] === 'CM.CaseFile') this.canAddNote = this.env.application.code === 'Portal.Providers' || this.accountContext.hasPermission('CM.CaseNote.Insert.SummaryInsert');
    }

    protected targetIdChanged(): void {
        this.deferReload();
    }

    protected pageSizeChanged(): void {
        this.deferReload();
    }

    @defer
    protected deferReload(): void {
        this.reload();
    }

    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            const sortDirection = direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending;

            if(sorting.entries.findIndex(entry => entry.reference === property && entry.direction === sortDirection) > -1) {
                return;
            }
            else {
                sorting.add({
                    reference: property,
                    direction: sortDirection
                });
            }
        }
        await this.reload();
    }

    protected openNoteHistory(note: Core.Note) {
        if(!note.id) return;
        this.$noteHistory.note = note;
        this.$noteHistoryModal.open();
    }

    protected saveNote(note: Core.Note | null): void {
        this.modalOpened = true;
        this.editedNote = note;
        this.$saveNoteModal.open();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadNotes(): Promise<void> {
        if(!this.targetId) return;

        const options: StandardLoadOptions = {
            sorting: this.sorting,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            includeInactive: this.includeInactive
        };

        const includes = [
            'category',
            'category.subCategory',
            'entered.byUser',
            'history',
            'metadata.created.byUser' // used by note-history
        ];

        const fields = [
            'category.description',
            'category.subCategory.description',
            'entered.byUser.description',
            'entered.dateTime',
            'isCustom',
            'metadata.created.byUser.description',  // used by note-history
            'metadata.created.timestamp',           // used by note-history
            'text'
        ];

        if(this.includeInactive) {
            includes.push('metadata.activeChanged.byUser');
            fields.push(
                'metadata.activeChanged.byUser.name.first',
                'metadata.activeChanged.byUser.name.full',
                'metadata.activeChanged.byUser.name.last',
                'metadata.activeChanged.deactivatedReason',
                'metadata.activeChanged.timestamp'
            );
        }
        if(this.notesConfig.includeRollup) {
            fields.push('attachedTo.type.code');
        }
        if(this.showTaskColumn) {
            includes.push('metadata.created.byTask', 'metadata.created.byTask.attachedTo.type.designation');
            fields.push('metadata.created.byTask.description', 'metadata.created.byTask.attachedTo.type.designation.description');
        }
        if(this.showFlagsColumn && !this.notesConfig.flagCodes) {
            includes.push('flags');
            fields.push('flags.description');
        }

        try {
            this.state.beginLoad();

            const response = await this.service.loadNotesByTarget(this.targetId, options, includes, this.appliedSearchFilters, fields);
            this.cache.notes = response.results;
            this.cache.targetId = this.targetId;
            this.cache.includeInactive = this.includeInactive;
            this.cache.sorting = this.sorting;
            this.cache.page = this.$pager?.page || 0;
            this.cache.resultCount = response.count || 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async activateNote(note: Core.Note): Promise<void> {
        // Show confirmation dialog only if deactivating
        let result: IConfirmationDialogResult = {
            reason: '',
            dialogResponse: ConfirmationOptions.OK
        };
        if(note.isActive) {
            result = await confirmation.showDeactivate('core:note.$', 1, true);
            if(result.dialogResponse !== confirmation.options.OK || !result.reason) return;
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.activateNote(note.id!, !note.isActive, result.reason!);
            isSuccessful = response.isSuccessful;
            this.events.publish(new NotesReloadEvent());
            await this.reload();
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.activate('core:note.$', !note.isActive, isSuccessful);
            this.state.endSave();
        }
    }

}

export interface INotesModelData {
    targetId: string;
    canEditNotes: boolean;
    showInactiveToggle: boolean;
    includeInactive: boolean;
    allowDeactivate: boolean;
    notesConfig: INotesConfig;
    readonly: boolean;
};

/**
    * Opens the notes grid modal with the global dialog
    * @param targetId The targetId to load collection notes
    * @param notesConfig The notesConfig to set up the note-service api calls
    * @param dialogOptions The dialogOptions to set the display of the global modal
    * @param canEditNotes notes-grid option to allow users to edit notes in the grid
    * @param showInactiveToggle notes-grid option to display inactive rows toggle
    * @param includeInactive notes-grid option to include inactive rows in the grid
    * @param allowDeactivate notes-grid option to allow for deactivation/reactivation of notes
    */
export function openNotes(targetId: string, notesConfig: INotesConfig, dialogOptions: IDialogOptions, canEditNotes: boolean = false, showInactiveToggle: boolean = false, includeInactive: boolean = false, allowDeactivate: boolean = false, readonly: boolean = false): void {
    if(!targetId || !notesConfig || !dialogOptions) {
        throw new Error('targetId, notesConfig, or dialogOptions are missing or invalid');
    }

    const modelData: INotesModelData = {
        targetId, canEditNotes, showInactiveToggle, includeInactive, allowDeactivate, notesConfig, readonly
    };

    const modalData: IModalData = {
        componentId: `app/core/components/notes/notes-grid`,
        modelData: modelData,
        dialogOptions: dialogOptions
    };

    openGlobalModal(modalData);
}
